import React, { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Truck, Wrench, Award, Users, MapPin, Phone, Mail, Menu, X, Flame, Construction, Shield, ChevronDown, ChevronLeft, ChevronRight, Volume2, VolumeX } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContent();
  }, []);

  const loadContent = async () => {
    try {
      const response = await axios.get(`${API}/content`);
      setContent(response.data);
    } catch (error) {
      console.error('Failed to load content:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
      setMobileMenuOpen(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-400 mx-auto mb-4"></div>
          <p className="text-gray-400 text-xl">Loading...</p>
        </div>
      </div>
    );
  }

  if (!content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center">
        <p className="text-red-400 text-xl">Failed to load content</p>
      </div>
    );
  }

  return (
    <div className="App">
      <Navigation 
        scrollToSection={scrollToSection} 
        activeSection={activeSection}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
      />
      <Hero scrollToSection={scrollToSection} content={content} />
      <Services content={content} />
      <Equipment content={content} />
      <PrizeDraw />
      <About content={content} />
      <Careers />
      <Quote />
      <Contact content={content} />
      <Footer scrollToSection={scrollToSection} content={content} />
    </div>
  );
}

const Navigation = ({ scrollToSection, activeSection, mobileMenuOpen, setMobileMenuOpen }) => {
  const navItems = [
    { id: 'home', label: 'Home' },
    { id: 'services', label: 'Services' },
    { id: 'equipment', label: 'Equipment' },
    { id: 'prize-draw', label: 'Prize Draw' },
    { id: 'about', label: 'About' },
    { id: 'careers', label: 'Careers' },
    { id: 'contact', label: 'Contact' },
  ];

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-gray-900/95 backdrop-blur-sm border-b border-green-500/20" data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center cursor-pointer" onClick={() => scrollToSection('home')} data-testid="logo-button">
            <img 
              src="/derose-logo.png" 
              alt="De Rose Transport" 
              className="h-16 w-auto"
              style={{filter: 'drop-shadow(0 0 10px rgba(0, 255, 65, 0.3))'}}
            />
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                data-testid={`nav-${item.id}`}
                className={`px-4 py-2 rounded-lg font-semibold transition-all duration-300 ${
                  activeSection === item.id
                    ? 'bg-green-500 text-gray-900'
                    : 'text-gray-300 hover:text-green-400 hover:bg-gray-800'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-green-400 p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-gray-900 border-t border-green-500/20" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-2">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => scrollToSection(item.id)}
                data-testid={`mobile-nav-${item.id}`}
                className={`w-full text-left px-4 py-3 rounded-lg font-semibold transition-all duration-300 ${
                  activeSection === item.id
                    ? 'bg-green-500 text-gray-900'
                    : 'text-gray-300 hover:text-green-400 hover:bg-gray-800'
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </nav>
  );
};

const Hero = ({ scrollToSection, content }) => {
  return (
    <section id="home" className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-black pt-20" data-testid="hero-section">
      <div className="absolute inset-0 bg-grid-pattern opacity-5"></div>
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center">
        <div className="mb-8">
          <img 
            src="/derose-logo.png" 
            alt="De Rose Transport" 
            className="h-40 md:h-52 w-auto mx-auto mb-6"
            style={{filter: 'drop-shadow(0 0 8px rgba(0, 255, 65, 0.3))'}}
            data-testid="hero-logo"
          />
          <h1 className="text-5xl md:text-7xl font-black mb-6 leading-tight" data-testid="hero-title">
            <span className="text-gray-100">{content.hero_title}</span>
          </h1>
          <p className="text-2xl md:text-3xl font-black mb-4 neon-flicker" data-testid="hero-subtitle">
            {content.hero_subtitle}
          </p>
          <p className="text-xl md:text-2xl text-gray-300 mb-8 whitespace-pre-line" data-testid="hero-description">
            {content.hero_description}
            {'\n'}
            <span className="text-green-400">{content.hero_location}</span>
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
          <button
            onClick={() => scrollToSection('quote')}
            data-testid="hero-quote-button"
            className="bg-green-500 hover:bg-green-400 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-green-500/50"
          >
            Request a Quote
          </button>
          <button
            onClick={() => scrollToSection('prize-draw')}
            data-testid="hero-prize-button"
            className="bg-gray-700 hover:bg-gray-600 px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg border-2"
            style={{color: '#01F9C6', borderColor: 'rgba(1, 249, 198, 0.3)', boxShadow: '0 10px 15px -3px rgba(1, 249, 198, 0.5)'}}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = '#01F9C6'}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = 'rgba(1, 249, 198, 0.3)'}
          >
            Enter Prize Draw 🎫
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-gray-800/50 backdrop-blur-sm border border-green-500/30 p-6 rounded-lg" data-testid="hero-feature-24-7">
            <div className="text-green-400 text-4xl mb-2">24/7</div>
            <div className="text-gray-300 font-semibold">Service Available</div>
          </div>
          <div className="bg-gray-800/50 backdrop-blur-sm border border-green-500/30 p-6 rounded-lg" data-testid="hero-feature-certified">
            <div className="text-green-400 text-4xl mb-2">✓</div>
            <div className="text-gray-300 font-semibold">Fully Certified & Insured</div>
          </div>
          <div className="bg-gray-800/50 backdrop-blur-sm border border-green-500/30 p-6 rounded-lg" data-testid="hero-feature-experience">
            <div className="text-4xl mb-2 flex gap-1 justify-center">
              {[...Array(5)].map((_, i) => (
                <span key={i} className="text-green-400" style={{filter: 'drop-shadow(0 0 5px #00ff41)'}}>★</span>
              ))}
            </div>
            <div className="text-gray-300 font-semibold">Experienced Crews</div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Services = ({ content }) => {
  const icons = [
    <Flame size={48} />,
    <Truck size={48} />,
    <Wrench size={48} />,
    <Construction size={48} />,
    <MapPin size={48} />,
    <Users size={48} />
  ];
  
  const services = content.services.map((service, index) => ({
    icon: icons[index] || <Truck size={48} />,
    title: service.title,
    description: service.description
  }));

  return (
    <section id="services" className="py-20 bg-gray-900" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-gray-100 mb-4" data-testid="services-title">
            Our <span className="text-green-400 glow-text">Services</span>
          </h2>
          <div className="w-24 h-1 bg-green-500 mx-auto mb-4"></div>
          <p className="text-xl text-gray-400" data-testid="services-subtitle">Professional transport and lifting solutions</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              data-testid={`service-card-${index}`}
              className="bg-gray-800 border border-green-500/20 p-8 rounded-lg hover:border-green-500 transition-all duration-300 transform hover:-translate-y-2 hover:shadow-xl hover:shadow-green-500/20"
            >
              <div className="text-green-400 mb-4">{service.icon}</div>
              <h3 className="text-2xl font-bold text-gray-100 mb-4">{service.title}</h3>
              <p className="text-gray-400">{service.description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};


// Equipment Carousel Component with Mixed Media Support (Photos + Videos)
const EquipmentCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [media, setMedia] = useState([]);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [isInView, setIsInView] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const carouselRef = useRef(null);
  const videoRefs = useRef([]);

  // Load equipment media from database
  useEffect(() => {
    const loadMedia = async () => {
      try {
        const response = await axios.get(`${API}/equipment-media`);
        if (response.data && response.data.length > 0) {
          setMedia(response.data.map(item => ({
            type: item.type,
            src: item.url
          })));
        } else {
          // Fallback to sample media if no uploads yet
          setMedia([
            { type: 'image', src: "https://images.unsplash.com/photo-1519003722824-194d4455a60c?w=800&h=400&fit=crop" },
            { type: 'image', src: "https://images.unsplash.com/photo-1615461066841-6116e61058f4?w=800&h=400&fit=crop" },
            { type: 'video', src: "https://videos.pexels.com/video-files/3129957/3129957-uhd_2560_1440_25fps.mp4" },
            { type: 'image', src: "https://images.unsplash.com/photo-1581094794329-c8112a89af12?w=800&h=400&fit=crop" },
            { type: 'video', src: "https://videos.pexels.com/video-files/2022395/2022395-uhd_2560_1440_24fps.mp4" },
            { type: 'image', src: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?w=800&h=400&fit=crop" }
          ]);
        }
      } catch (error) {
        console.error('Failed to load equipment media:', error);
        // Use fallback media on error
        setMedia([
          { type: 'image', src: "https://images.unsplash.com/photo-1519003722824-194d4455a60c?w=800&h=400&fit=crop" },
          { type: 'image', src: "https://images.unsplash.com/photo-1615461066841-6116e61058f4?w=800&h=400&fit=crop" }
        ]);
      }
    };
    loadMedia();
  }, []);

  const nextSlide = () => {
    if (media.length > 3) {
      setCurrentSlide((prev) => prev + 1);
    }
  };

  const prevSlide = () => {
    if (media.length > 3) {
      setCurrentSlide((prev) => prev - 1);
    }
  };

  // Handle infinite loop reset - smoother approach
  useEffect(() => {
    if (media.length <= 3) return;
    
    // If we've scrolled to show the cloned items, instantly jump to real position
    if (currentSlide >= media.length) {
      // Disable transition, jump to position 0, re-enable transition
      const timer = setTimeout(() => {
        setIsTransitioning(false);
        requestAnimationFrame(() => {
          setCurrentSlide(0);
          requestAnimationFrame(() => {
            setIsTransitioning(true);
          });
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [currentSlide, media.length]);

  // Intersection Observer to detect when carousel is in view
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      {
        threshold: 0.3, // Trigger when 30% of carousel is visible
        rootMargin: '0px'
      }
    );

    if (carouselRef.current) {
      observer.observe(carouselRef.current);
    }

    return () => {
      if (carouselRef.current) {
        observer.unobserve(carouselRef.current);
      }
    };
  }, []);

  // Auto-scroll every 3 seconds (pauses when video is playing or not in view)
  useEffect(() => {
    if (!isVideoPlaying && isInView && media.length > 3) {
      const interval = setInterval(nextSlide, 3000);
      return () => clearInterval(interval);
    }
  }, [isVideoPlaying, isInView, media.length]);

  // Update video mute state when isMuted changes
  useEffect(() => {
    // Find the currently playing video (centered position)
    const centerIndex = currentSlide + 1;
    const video = videoRefs.current[centerIndex];
    
    if (video && isVideoPlaying) {
      video.muted = isMuted;
      video.volume = isMuted ? 0 : 0.7;
    }
  }, [isMuted, currentSlide, isVideoPlaying]);

  // Handle video playback
  useEffect(() => {
    if (media.length === 0) return;
    
    // Reset video playing state
    setIsVideoPlaying(false);
    setVideoProgress(0);
    
    // Calculate center position in the extended array
    const centerIndex = currentSlide + 1;
    
    videoRefs.current.forEach((video, index) => {
      if (video) {
        const isCenter = index === centerIndex;
        const actualMediaIndex = index % media.length;
        const isVideo = media[actualMediaIndex]?.type === 'video';
        
        if (isCenter && isVideo) {
          // Play video in center position
          video.muted = isMuted;
          video.volume = isMuted ? 0 : 0.7;
          video.play().then(() => {
            setIsVideoPlaying(true);
          }).catch(err => {
            console.log('Video autoplay prevented:', err);
            // If autoplay with sound is blocked, try muted
            video.muted = true;
            video.play().then(() => {
              setIsVideoPlaying(true);
            }).catch(() => {
              setIsVideoPlaying(false);
            });
          });
        } else {
          // Pause and reset videos not in center
          video.pause();
          video.currentTime = 0;
        }
      }
    });
  }, [currentSlide, media, isMuted]);

  // Update video progress
  const handleVideoTimeUpdate = (e) => {
    const video = e.target;
    const progress = (video.currentTime / video.duration) * 100;
    setVideoProgress(progress);
  };

  // Handle video end - advance carousel
  const handleVideoEnd = () => {
    setIsVideoPlaying(false);
    setVideoProgress(0);
    setTimeout(() => {
      nextSlide();
    }, 500); // Small delay before advancing
  };

  // Touch handlers for mobile swipe
  const handleTouchStart = (e) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (touchStart - touchEnd > 75) {
      nextSlide();
    }
    if (touchStart - touchEnd < -75) {
      prevSlide();
    }
  };

  return (
    <div ref={carouselRef} className="max-w-6xl mx-auto relative mb-8">
      {media.length === 0 ? (
        <div className="text-center text-gray-400 py-12">
          <p>No equipment media uploaded yet</p>
        </div>
      ) : media.length < 3 ? (
        // Show single or two items centered without carousel
        <div className="flex justify-center gap-4">
          {media.map((item, idx) => (
            <div key={idx} className="w-full max-w-md h-48 md:h-64 rounded-lg overflow-hidden border-2 border-green-500/30 bg-black">
              {item.type === 'image' ? (
                <img 
                  src={item.src} 
                  alt={`Equipment ${idx + 1}`} 
                  className="w-full h-full object-cover" 
                />
              ) : (
                <video
                  src={item.src}
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                />
              )}
            </div>
          ))}
        </div>
      ) : (
        // Full carousel with 3+ items
        <>
      <div 
        className="overflow-hidden rounded-lg"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          className={`flex gap-4 ${isTransitioning ? 'transition-transform duration-500 ease-in-out' : ''}`}
          style={{ transform: `translateX(-${currentSlide * 33.33}%)` }}
        >
          {/* Create an extended array for seamless looping */}
          {[...media, ...media.slice(0, 2)].map((item, idx) => {
            const actualIndex = idx % media.length;
            return (
            <div key={`${actualIndex}-${idx}`} className="min-w-[calc(33.33%-0.67rem)] h-48 md:h-64 rounded-lg overflow-hidden border-2 border-green-500/30 hover:border-green-500 transition-all bg-black relative">
              {item.type === 'image' ? (
                <img 
                  src={item.src} 
                  alt={`Equipment ${actualIndex + 1}`} 
                  className={`w-full h-full bg-gray-900 ${
                    actualIndex === 0 || actualIndex === 4 ? 'object-contain' : 'object-cover'
                  }`}
                  style={actualIndex === 4 ? {transform: 'scale(1.3)'} : {}}
                />
              ) : (
                <>
                  <video
                    ref={(el) => (videoRefs.current[idx] = el)}
                    src={item.src}
                    className="w-full h-full object-cover"
                    muted={isMuted}
                    playsInline
                    onEnded={handleVideoEnd}
                    onTimeUpdate={handleVideoTimeUpdate}
                  />
                  
                  {/* Video loading overlay - shows play icon when not playing */}
                  {!isVideoPlaying && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                      <div className="bg-green-500 rounded-full p-3 shadow-lg">
                        <svg className="w-8 h-8 text-gray-900" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z"/>
                        </svg>
                      </div>
                    </div>
                  )}
                  
                  {/* Video Progress Bar - only shows on the video thumbnail when playing */}
                  {isVideoPlaying && idx === currentSlide + 1 && (
                    <>
                      {/* Volume Control Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setIsMuted(!isMuted);
                        }}
                        onTouchEnd={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          setIsMuted(!isMuted);
                        }}
                        className="absolute top-2 right-2 z-20 bg-green-500 hover:bg-green-400 text-gray-900 p-2 rounded-full shadow-lg transition-all"
                        aria-label={isMuted ? "Unmute" : "Mute"}
                      >
                        {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                      </button>

                      {/* Progress Bar */}
                      <div className="absolute bottom-2 left-2 right-2 z-20">
                      <div className="relative h-2 bg-gray-800/80 rounded-full overflow-visible backdrop-blur-sm border border-green-500/30">
                        {/* Progress fill */}
                        <div 
                          className="absolute inset-0 bg-gradient-to-r from-green-500 to-green-400 rounded-full transition-all duration-200"
                          style={{ width: `${videoProgress}%` }}
                        />
                        
                        {/* Circular truck icon moving across the bar */}
                        <div 
                          className="absolute top-1/2 -translate-y-1/2 transition-all duration-200"
                          style={{ 
                            left: `${videoProgress}%`,
                            transform: `translate(-50%, -50%)`
                          }}
                        >
                          <div className="w-6 h-6 md:w-7 md:h-7 rounded-full overflow-hidden bg-white border-2 border-green-400 shadow-lg flex items-center justify-center">
                            <img 
                              src="/truck-icon.jpg" 
                              alt="Truck" 
                              className="w-full h-full object-cover"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                    </>
                  )}
                </>
              )}
            </div>
            );
          })}
        </div>
      </div>

      {/* Navigation Arrows - always enabled */}
      <button
        onClick={prevSlide}
        className="absolute left-2 top-1/2 -translate-y-1/2 bg-green-500 hover:bg-green-400 text-white p-2 md:p-3 rounded-full transition-all z-10"
        aria-label="Previous"
      >
        <ChevronLeft size={20} />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-2 top-1/2 -translate-y-1/2 bg-green-500 hover:bg-green-400 text-white p-2 md:p-3 rounded-full transition-all z-10"
        aria-label="Next"
      >
        <ChevronRight size={20} />
      </button>
      </>
      )}
    </div>
  );
};

const Equipment = ({ content }) => {
  const transportEquipment = content.transport_equipment || [];
  const rentalEquipment = content.rental_equipment || [];

  return (
    <section id="equipment" className="py-20 bg-black" data-testid="equipment-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-gray-100 mb-4" data-testid="equipment-title">
            Our <span className="text-green-400 glow-text">Equipment</span>
          </h2>
          <div className="w-24 h-1 bg-green-500 mx-auto mb-4"></div>
          <p className="text-xl text-gray-400" data-testid="equipment-subtitle">SAGD, Oilfield, Plant & Off Roads Ready</p>
        </div>

        {/* Equipment Image Carousel */}
        <EquipmentCarousel />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mt-12">
          {/* Transport Equipment */}
          <div className="bg-gray-900 border border-green-500/30 p-8 rounded-lg" data-testid="transport-equipment">
            <h3 className="text-3xl font-bold text-white mb-6 flex items-center">
              <Truck className="mr-3 text-green-400" size={32} />
              {content.transport_equipment_title || "Transport Equipment"}
            </h3>
            <div className="max-h-96 overflow-y-auto pr-2 custom-scrollbar">
              <ul>
                {transportEquipment.map((item, index) => {
                  const noBullet = item.startsWith('NOBULLET:');
                  const displayItem = noBullet ? item.replace('NOBULLET:', '') : item;
                  const prevItem = index > 0 ? transportEquipment[index - 1] : null;
                  const prevNoBullet = prevItem && prevItem.startsWith('NOBULLET:');
                  
                  // Apply tight spacing for all non-bullet items, normal spacing only for bullet items
                  const marginClass = noBullet ? 'mt-1' : 'mt-3';
                  
                  return (
                    <li key={index} className={`flex items-start ${index === 0 ? '' : marginClass}`} data-testid={`transport-item-${index}`}>
                      {!noBullet && <span className="text-green-400 mr-3 mt-1">▸</span>}
                      <span className={`text-gray-300 ${noBullet ? 'ml-6' : ''}`} dangerouslySetInnerHTML={{ __html: displayItem }}></span>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* Rental Equipment */}
          <div className="bg-gray-900 border border-green-500/30 p-8 rounded-lg" data-testid="rental-equipment">
            <h3 className="text-3xl font-bold text-white mb-6 flex items-center">
              <Wrench className="mr-3 text-green-400" size={32} />
              {content.rental_equipment_title || "Rental Equipment"}
            </h3>
            <div className="max-h-96 overflow-y-auto pr-2 custom-scrollbar">
              <ul className="space-y-3">
                {rentalEquipment.map((item, index) => (
                  <li key={index} className="flex items-start" data-testid={`rental-item-${index}`}>
                    <span className="text-green-400 mr-3 mt-1">▸</span>
                    <span className="text-gray-300 whitespace-pre-line">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-12 bg-gray-900 border border-yellow-500/30 p-8 rounded-lg" data-testid="equipment-features">
          <h3 className="text-2xl font-bold text-white mb-4 flex items-center">
            <Shield className="mr-3 text-green-400" size={28} />
            Safety & Compliance
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-gray-300">
            {(content.safety_compliance_items || []).map((item, index) => (
              <div key={index} className="flex items-start">
                <span className="text-green-400 mr-3">✓</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const PrizeDraw = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    entry_number: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess(false);

    try {
      // Submit with swag_only type and SWAG-ONLY entry number
      const submitData = {
        entry_type: 'swag_only',
        entry_number: formData.entry_number || 'SWAG-ONLY',
        first_name: formData.name.split(' ')[0] || '',
        last_name: formData.name.split(' ').slice(1).join(' ') || '',
        email: formData.email || 'not-provided@example.com',
        phone: formData.phone || 'Not provided',
      };
      
      await axios.post(`${API}/prize-draw`, submitData);
      setSuccess(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        entry_number: '',
      });
    } catch (err) {
      setError('Failed to submit entry. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="prize-draw" className="py-20 bg-gradient-to-br from-cyan-900/20 via-gray-900 to-gray-900" data-testid="prize-draw-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <div className="inline-block text-gray-900 px-6 py-2 rounded-full font-bold text-sm mb-4 animate-pulse" style={{backgroundColor: '#01F9C6'}}>EXCLUSIVE OFFER</div>
          <h2 className="text-4xl md:text-5xl font-black text-gray-100 mb-4" data-testid="prize-draw-title">
            <span className="glow-text" style={{color: '#01F9C6', textShadow: '0 0 20px #01F9C6, 0 0 10px #01F9C6'}}>Swag Draw</span>
          </h2>
          <div className="w-24 h-1 mx-auto mb-4" style={{backgroundColor: '#01F9C6'}}></div>
          <p className="text-xl text-gray-300" data-testid="prize-draw-subtitle">
            Fill in your info to enter Swag Draw
          </p>
        </div>

        <div className="bg-gray-800 rounded-2xl p-8 shadow-2xl" style={{borderWidth: '2px', borderColor: 'rgba(1, 249, 198, 0.5)', boxShadow: '0 25px 50px -12px rgba(1, 249, 198, 0.2)'}}>
          {success ? (
            <div className="text-center py-12" data-testid="prize-draw-success">
              <Award className="text-green-400 mx-auto mb-4" size={64} />
              <h3 className="text-3xl font-bold text-green-400 mb-4">Entry Submitted!</h3>
              <p className="text-gray-300 mb-6">Good luck! We'll contact you if you win.</p>
              
              <div className="mb-6 p-6 rounded-lg" style={{backgroundColor: 'rgba(1, 249, 198, 0.1)', borderWidth: '2px', borderColor: '#01F9C6'}}>
                <p className="text-lg text-gray-100 mb-4 font-semibold">Help us improve & boost your chances!</p>
                <a
                  href="https://g.page/r/CT75i3rrbUGTEBE/review"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="prize-draw-review-button"
                  className="inline-block text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
                  style={{backgroundColor: '#01F9C6', boxShadow: '0 10px 15px -3px rgba(1, 249, 198, 0.5)'}}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#00E5B4'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#01F9C6'}
                >
                  ⭐ Leave us a Google Review
                </a>
              </div>
              
              <button
                onClick={() => setSuccess(false)}
                data-testid="prize-draw-submit-another"
                className="bg-gray-700 hover:bg-gray-600 text-green-400 px-6 py-3 rounded-lg font-bold transition-all border-2 border-green-500/30"
              >
                Submit Another Entry
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="prize-draw-form">
              <div className="mb-6">
                <label className="block text-gray-300 font-semibold mb-2">Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="name-input"
                  className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                  placeholder="Your name"
                />
              </div>

              <div className="mb-6">
                <label className="block text-gray-300 font-semibold mb-2">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="email-input"
                  className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                  placeholder="your@email.com"
                />
              </div>

              <div className="mb-6">
                <label className="block text-gray-300 font-semibold mb-2">Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="phone-input"
                  className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                  placeholder="780-555-1234"
                />
              </div>

              <div className="mb-6">
                <label className="block text-gray-300 font-semibold mb-2">Invoice # or Field Ticket BOL #</label>
                <input
                  type="text"
                  name="entry_number"
                  value={formData.entry_number}
                  onChange={handleChange}
                  data-testid="entry-number-input"
                  className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                  placeholder="Optional"
                />
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400" data-testid="prize-draw-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="prize-draw-submit-button"
                className="w-full text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                style={{backgroundColor: '#01F9C6', boxShadow: submitting ? '' : '0 10px 15px -3px rgba(1, 249, 198, 0.5)'}}
                onMouseEnter={(e) => !submitting && (e.currentTarget.style.backgroundColor = '#00E5B4')}
                onMouseLeave={(e) => !submitting && (e.currentTarget.style.backgroundColor = '#01F9C6')}
              >
                {submitting ? 'Submitting...' : '🎫 Enter Prize Draw'}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};

const About = ({ content }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <section id="about" className="py-20 bg-gray-900" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          className="text-center mb-16 cursor-pointer" 
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <h2 className="text-4xl md:text-5xl font-black text-gray-100" data-testid="about-title">
              {content.about_title.split(' ')[0]} <span className="text-green-400 glow-text">{content.about_title.split(' ').slice(1).join(' ')}</span>
            </h2>
            <ChevronDown 
              className={`text-green-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
              size={32} 
            />
          </div>
          <div className="w-24 h-1 bg-green-500 mx-auto"></div>
        </div>

        {isOpen && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div data-testid="about-content">
            <h3 className="text-3xl font-bold text-yellow-400 mb-6">{content.hero_subtitle}</h3>
            <div className="space-y-4 text-gray-300 text-lg">
              <p>{content.about_content}</p>
              <p className="text-green-400 font-semibold">
                Available 24/7 to meet your project needs.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-6" data-testid="about-stats">
            <div className="bg-gray-800 border border-green-500/30 p-6 rounded-lg text-center">
              <div className="text-4xl font-black text-green-400 mb-2">24/7</div>
              <div className="text-gray-400">Service</div>
            </div>
            <div className="bg-gray-800 border border-green-500/30 p-6 rounded-lg text-center">
              <div className="text-4xl font-black text-green-400 mb-2">45T</div>
              <div className="text-gray-400">Max Capacity</div>
            </div>
            <div className="bg-gray-800 border border-green-500/30 p-6 rounded-lg text-center">
              <div className="text-4xl font-black text-green-400 mb-2">100%</div>
              <div className="text-gray-400">SAGD Ready</div>
            </div>
            <div className="bg-gray-800 border border-green-500/30 p-6 rounded-lg text-center">
              <div className="text-4xl font-black text-green-400 mb-2">WC</div>
              <div className="text-gray-400">Western Canada</div>
            </div>
          </div>
        </div>
        )}
      </div>
    </section>
  );
};

const Careers = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    position_interest: '',
    experience: '',
    additional_info: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess(false);

    try {
      // Save to database
      await axios.post(`${API}/career`, formData);
      
      // Create mailto link with form data
      const subject = encodeURIComponent(`Job Application - ${formData.position_interest}`);
      const body = encodeURIComponent(
        `New Career Application\n\n` +
        `Full Name: ${formData.full_name}\n` +
        `Email: ${formData.email}\n` +
        `Phone: ${formData.phone}\n` +
        `Position of Interest: ${formData.position_interest}\n\n` +
        `Experience & Qualifications:\n${formData.experience}\n\n` +
        `Additional Information:\n${formData.additional_info || 'N/A'}\n\n` +
        `---\nSubmitted via DeRose Transport website`
      );
      
      const mailtoLink = `mailto:HR@DeRoseTransport.com?subject=${subject}&body=${body}`;
      
      // Open mailto link
      window.location.href = mailtoLink;
      
      setSuccess(true);
      setFormData({
        full_name: '',
        email: '',
        phone: '',
        position_interest: '',
        experience: '',
        additional_info: '',
      });
    } catch (err) {
      setError('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="careers" className="py-20 bg-black" data-testid="careers-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          className="text-center mb-12 cursor-pointer" 
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <h2 className="text-4xl md:text-5xl font-black text-gray-100" data-testid="careers-title">
              Join Our <span className="text-green-400 glow-text">Team</span>
            </h2>
            <ChevronDown 
              className={`text-green-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
              size={32} 
            />
          </div>
          <div className="w-24 h-1 bg-green-500 mx-auto mb-4"></div>
          <p className="text-xl text-gray-300" data-testid="careers-subtitle">Build your career with a family-owned company</p>
        </div>

        {isOpen && (
          <div className="bg-gray-900 border border-green-500/30 rounded-lg p-8">
          {success ? (
            <div className="text-center py-12" data-testid="careers-success">
              <Users className="text-green-400 mx-auto mb-4" size={64} />
              <h3 className="text-3xl font-bold text-green-400 mb-4">Application Received!</h3>
              <p className="text-gray-300 mb-6">Thank you for your interest. We'll review your application and be in touch soon.</p>
              <button
                onClick={() => setSuccess(false)}
                data-testid="careers-submit-another"
                className="bg-green-500 hover:bg-green-400 text-gray-900 px-6 py-3 rounded-lg font-bold transition-all"
              >
                Submit Another Application
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="careers-form">
              <div className="mb-6">
                <label className="block text-gray-300 font-semibold mb-2">Full Name</label>
                <input
                  type="text"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  data-testid="career-name-input"
                  className="w-full px-4 py-3 bg-gray-800 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="career-email-input"
                    className="w-full px-4 py-3 bg-gray-800 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="career-phone-input"
                    className="w-full px-4 py-3 bg-gray-800 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-gray-300 font-semibold mb-2">Position of Interest</label>
                <input
                  type="text"
                  name="position_interest"
                  value={formData.position_interest}
                  onChange={handleChange}
                  required
                  data-testid="career-position-input"
                  placeholder="e.g., Truck Driver, Heavy Equipment Operator, Rigger"
                  className="w-full px-4 py-3 bg-gray-800 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                />
              </div>

              <div className="mb-6">
                <label className="block text-gray-300 font-semibold mb-2">Experience & Qualifications</label>
                <textarea
                  name="experience"
                  value={formData.experience}
                  onChange={handleChange}
                  required
                  data-testid="career-experience-input"
                  rows="4"
                  placeholder="Tell us about your experience, certifications, and qualifications"
                  className="w-full px-4 py-3 bg-gray-800 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                />
              </div>

              <div className="mb-8">
                <label className="block text-gray-300 font-semibold mb-2">Additional Information (Optional)</label>
                <textarea
                  name="additional_info"
                  value={formData.additional_info}
                  onChange={handleChange}
                  data-testid="career-additional-input"
                  rows="3"
                  className="w-full px-4 py-3 bg-gray-800 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                />
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400" data-testid="careers-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="careers-submit-button"
                className="w-full bg-green-500 hover:bg-green-400 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-green-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit Application'}
              </button>
            </form>
          )}
        </div>
        )}
      </div>
    </section>
  );
};

const Quote = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    contact_name: '',
    email: '',
    phone: '',
    service_type: '',
    project_details: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    setSuccess(false);

    try {
      // Save to database
      await axios.post(`${API}/quote`, formData);
      
      // Create mailto link with form data
      const subject = encodeURIComponent(`Quote Request - ${formData.service_type}`);
      const body = encodeURIComponent(
        `New Quote Request\n\n` +
        `Company Name: ${formData.company_name}\n` +
        `Contact Name: ${formData.contact_name}\n` +
        `Email: ${formData.email}\n` +
        `Phone: ${formData.phone}\n` +
        `Service Type: ${formData.service_type}\n\n` +
        `Project Details:\n${formData.project_details}\n\n` +
        `---\nSubmitted via DeRose Transport website`
      );
      
      const mailtoLink = `mailto:WebDispatch@DeRoseTransport.com?subject=${subject}&body=${body}`;
      
      // Open mailto link
      window.location.href = mailtoLink;
      
      setSuccess(true);
      setFormData({
        company_name: '',
        contact_name: '',
        email: '',
        phone: '',
        service_type: '',
        project_details: '',
      });
    } catch (err) {
      setError('Failed to submit quote request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const serviceTypes = [
    'Oilfield Transport',
    'Lifting & Transportation',
    'Pickers & Cranes',
    'Off Highway & Remote Access',
    'Logistics & Warehousing',
    'Equipment Rental',
    'Other',
  ];

  return (
    <section id="quote" className="py-20 bg-gray-900" data-testid="quote-section">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div 
          className="text-center mb-12 cursor-pointer" 
          onClick={() => setIsOpen(!isOpen)}
        >
          <div className="flex items-center justify-center gap-3 mb-4">
            <h2 className="text-4xl md:text-5xl font-black text-gray-100" data-testid="quote-title">
              Request a <span className="text-green-400 glow-text">Quote</span>
            </h2>
            <ChevronDown 
              className={`text-green-400 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
              size={32} 
            />
          </div>
          <div className="w-24 h-1 bg-green-500 mx-auto mb-4"></div>
          <p className="text-xl text-gray-300" data-testid="quote-subtitle">Get a custom quote for your project</p>
        </div>

        {isOpen && (
          <div className="bg-gray-800 border border-green-500/30 rounded-lg p-8">
          {success ? (
            <div className="text-center py-12" data-testid="quote-success">
              <Truck className="text-green-400 mx-auto mb-4" size={64} />
              <h3 className="text-3xl font-bold text-green-400 mb-4">Quote Request Received!</h3>
              <p className="text-gray-300 mb-6">We'll review your request and get back to you within 24 hours.</p>
              <button
                onClick={() => setSuccess(false)}
                data-testid="quote-submit-another"
                className="bg-green-500 hover:bg-green-400 text-gray-900 px-6 py-3 rounded-lg font-bold transition-all"
              >
                Submit Another Request
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} data-testid="quote-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Company Name</label>
                  <input
                    type="text"
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    required
                    data-testid="quote-company-input"
                    className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Contact Name</label>
                  <input
                    type="text"
                    name="contact_name"
                    value={formData.contact_name}
                    onChange={handleChange}
                    required
                    data-testid="quote-contact-input"
                    className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    data-testid="quote-email-input"
                    className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 font-semibold mb-2">Phone</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    data-testid="quote-phone-input"
                    className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                  />
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-gray-300 font-semibold mb-2">Service Type</label>
                <select
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleChange}
                  required
                  data-testid="quote-service-input"
                  className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                >
                  <option value="">Select a service</option>
                  {serviceTypes.map((service) => (
                    <option key={service} value={service}>
                      {service}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mb-8">
                <label className="block text-gray-300 font-semibold mb-2">Project Details</label>
                <textarea
                  name="project_details"
                  value={formData.project_details}
                  onChange={handleChange}
                  required
                  data-testid="quote-details-input"
                  rows="5"
                  placeholder="Please provide details about your project, timeline, and any specific requirements"
                  className="w-full px-4 py-3 bg-gray-900 border border-green-500/30 rounded-lg text-gray-100 focus:outline-none focus:border-green-500 transition-all"
                />
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-500/20 border border-red-500 rounded-lg text-red-400" data-testid="quote-error">
                  {error}
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                data-testid="quote-submit-button"
                className="w-full bg-green-500 hover:bg-green-400 text-gray-900 px-8 py-4 rounded-lg font-bold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-green-500/50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? 'Submitting...' : 'Submit Quote Request'}
              </button>
            </form>
          )}
        </div>
        )}
      </div>
    </section>
  );
};

const Contact = ({ content }) => {
  return (
    <section id="contact" className="py-20 bg-black" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-black text-gray-100 mb-4" data-testid="contact-title">
            Get in <span className="text-green-400 glow-text">Touch</span>
          </h2>
          <div className="w-24 h-1 bg-green-500 mx-auto"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Quick Response */}
          <div className="bg-gray-900 border border-green-500/30 p-8 rounded-lg text-center" data-testid="contact-phone">
            <Phone className="text-green-400 mx-auto mb-4" size={48} />
            <h3 className="text-2xl font-bold text-gray-100 mb-4">Quick Response</h3>
            <p className="text-gray-400 mb-4">Call us for immediate assistance</p>
            <div className="space-y-2">
              <a 
                href={`tel:${content.contact_phone1.replace(/-/g, '')}`}
                className="block text-green-400 hover:text-green-300 font-bold text-xl transition-all phone-protected"
                data-testid="contact-phone-1"
                style={{direction: 'rtl', unicodeBidi: 'bidi-override'}}
              >
                {content.contact_phone1.split('').reverse().join('')}
              </a>
              <a 
                href={`tel:${content.contact_phone2.replace(/-/g, '')}`}
                className="block text-green-400 hover:text-green-300 font-bold text-xl transition-all phone-protected"
                data-testid="contact-phone-2"
                style={{direction: 'rtl', unicodeBidi: 'bidi-override'}}
              >
                {content.contact_phone2.split('').reverse().join('')}
              </a>
            </div>
            <p className="text-green-400 font-semibold mt-4">24/7 Service</p>
          </div>

          {/* Email */}
          <a 
            href="mailto:WebDispatch@DeRoseTransport.com"
            className="bg-gray-900 border border-green-500/30 p-8 rounded-lg text-center block hover:border-green-500 transition-all cursor-pointer" 
            data-testid="contact-email"
          >
            <Mail className="text-green-400 mx-auto mb-4" size={48} />
            <h3 className="text-2xl font-bold text-gray-100 mb-4">Email Us</h3>
            <p className="text-gray-400">For detailed inquiries</p>
          </a>

          {/* Locations */}
          <div className="bg-gray-900 border border-green-500/30 p-8 rounded-lg text-center" data-testid="contact-address">
            <MapPin className="text-green-400 mx-auto mb-4" size={48} />
            <h3 className="text-2xl font-bold text-gray-100 mb-4">Locations</h3>
            <div className="text-gray-300 space-y-2">
              <p className="font-semibold text-green-400">Devon, AB</p>
              <p className="font-semibold text-green-400">Nisku, AB</p>
              <p className="font-semibold text-green-400 mt-3">Serving Western Canada</p>
            </div>
          </div>
        </div>

        {/* Certifications */}
        <div className="mt-16 text-center" data-testid="contact-certifications">
          <h3 className="text-2xl font-bold text-gray-100 mb-6">Certified & Accredited</h3>
          <div className="flex flex-wrap justify-center gap-6 text-gray-400">
            <div className="px-6 py-3 bg-gray-900 border border-green-500/20 rounded-lg">COR</div>
            <div className="px-6 py-3 bg-gray-900 border border-green-500/20 rounded-lg">WCB Alberta</div>
            <div className="px-6 py-3 bg-gray-900 border border-green-500/20 rounded-lg">ISNetworld</div>
            <div className="px-6 py-3 bg-gray-900 border border-green-500/20 rounded-lg">Avetta</div>
            <div className="px-6 py-3 bg-gray-900 border border-green-500/20 rounded-lg">ComplyWorks</div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = ({ scrollToSection, content }) => {
  return (
    <footer className="bg-gray-900 border-t border-green-500/20 py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-8">
          <div>
            <img 
              src="/derose-logo.png" 
              alt="De Rose Transport" 
              className="h-16 w-auto mb-4"
              style={{filter: 'drop-shadow(0 0 10px rgba(0, 255, 65, 0.3))'}}
            />
            <p className="text-gray-400">{content.footer_tagline}</p>
          </div>

          <div>
            <h4 className="text-gray-100 font-bold mb-4">Quick Links</h4>
            <div className="space-y-2">
              {['services', 'equipment', 'prize-draw', 'about', 'careers', 'contact'].map((section) => (
                <button
                  key={section}
                  onClick={() => scrollToSection(section)}
                  data-testid={`footer-link-${section}`}
                  className="block text-gray-400 hover:text-green-400 transition-all capitalize"
                >
                  {section.replace('-', ' ')}
                </button>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-gray-100 font-bold mb-4">Contact</h4>
            <div className="space-y-2 text-gray-400">
              <p>📞 <span style={{direction: 'rtl', unicodeBidi: 'bidi-override', display: 'inline-block'}}>6913-519-087</span></p>
              <p>📞 <span style={{direction: 'rtl', unicodeBidi: 'bidi-override', display: 'inline-block'}}>6061-122-087</span></p>
              <p>📧 WebDispatch@DeRoseTransport.com</p>
              <p>📍 Devon, AB | Nisku, AB</p>
              <p>📍 Locations across Western Canada</p>
            </div>
          </div>
        </div>

        <div className="border-t border-green-500/20 pt-8 text-center text-gray-400">
          <p>&copy; {new Date().getFullYear()} De Rose Transport. All rights reserved.</p>
          <p className="mt-2 text-green-400 font-semibold">24/7 Service Available</p>
        </div>
      </div>
    </footer>
  );
};

export default App;